import ApiService from '@/common/api.service'

import * as utils from '@/common/utils'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    option: 'all', // all, selected
    // Event Profile Option
    ep_option: 'create', // create, de_allocate
    sourceClient: {
      id: null,
      name: null
    },
    destinationClient: {
      id: null,
      name: null
    },
    step: 1,
    can_continue: true,
    selected_vehicles: [],
    selected_drivers: [],
    overview: {}
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {
    clients: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param assets     Object
 *
 * @return Object
 */
function transformPost (assets) {
  return {
    option: assets.option,
    ep_option: assets.ep_option,
    source_client_id: assets.sourceClient.id,
    destination_client_id: assets.destinationClient.id,
    vehicles: assets.selected_vehicles,
    drivers: assets.selected_drivers
  }
}

function transformOverviewData (assets) {
  return {
    option: assets.option,
    ep_option: assets.ep_option,
    source_client_id: assets.sourceClient.id,
    destination_client_id: assets.destinationClient.id,
    vehicles: assets.selected_vehicles,
    drivers: assets.selected_drivers
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }
}

export const actions = {
  /**
   * Query all vehicles by client
   *
   * @param context Object
   *
   * @param params
   * @return Promise
   */
  queryVehiclesByClient (context, params) {
    let client_id = context.getters['get'].sourceClient.id
    return ApiService
      .get(`vehicles/by-client/${client_id}`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Query all drivers by client
   *
   * @param context Object
   *
   * @param params
   * @return Promise
   */
  queryDriversByClient (context, params) {
    let client_id = context.getters['get'].sourceClient.id
    return ApiService
      .get(`drivers/by-client/${client_id}`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Query all asset count overview
   *
   * @param context Object
   *
   * @return Promise
   */
  getAssetOverview (context) {
    context.commit('setIsLoading', true)
    let assets = context.getters['get']
    return ApiService
      .post(`clients/asset/transfer/overview`, transformOverviewData(assets))
      .then((response) => {
        context.commit('setOverview', response.data.data)
        context.commit('setIsLoading', false)
        return response
      })
  },

  assetTransfer (context) {
    context.commit('setIsLoading', true)
    let assets = context.getters['get']
    return ApiService
      .post(`clients/asset/transfer`, transformPost(assets))
      .then((response) => {
        context.commit('setIsLoading', false)
        context.commit('reset')
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let assets = context.getters['get']

    return ApiService
      .post(`clients/asset/transfer`, transformPost(assets))
      .then((response) => {
        context.commit('reset')
        return response
      })
  }
}

export const mutations = {
  setFormData (state, data) {
    state.form.clients = data.clients
  },

  setOverview (state, data) {
    state.data.overview = data
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/assets/asset-transfer/assetTransferModule.js